import { useEffect } from 'react';
import { useSnapshot } from 'valtio';

import Link from '@cartwhl/common/components/Link';
import { Job, ProjectType } from '@cartwhl/common/types';
import { getHomepageUrl, getStudioUrl } from '@cartwhl/common/utils';
import DaysAgo from '@cartwhl/ui/atoms/DaysAgo';
import Text from '@cartwhl/ui/base/Text';
import ProjectCard from '@cartwhl/ui/feature/ProjectCard';
import Button from '@cartwhl/ui/input/Button';

import { galleryStore, userStore } from 'stores/global';

import CurrentPlan from './CurrentPlan';
import GalleryList from './GalleryList';
import UserDetails from './UserDetails';

export default function Overview() {
  const { userInfo, downloadHistory } = useSnapshot(userStore.state);
  const { favoritesRecords } = useSnapshot(galleryStore.state);

  const records = (favoritesRecords.data?.items || []) as Job[];

  useEffect(() => {
    if (records.length === 0 && favoritesRecords.status === 'initial') {
      galleryStore.fetchFavorites(8, false);
    }
    if (downloadHistory.status === 'initial') {
      userStore.fetchDownloadHistory({ limit: 6, sortAscending: false });
    }
  }, [records.length, favoritesRecords.status, downloadHistory]);

  return (
    <div className="overview max-w-(--breakpoint-md) mx-auto">
      <UserDetails userInfo={userInfo} />
      <div className="mt-8">
        <Text as="h3" variant="studio/body/medium/sm" className="mb-4">
          Your plan
        </Text>
        <CurrentPlan />
      </div>
      <div className="mt-8">
        <div className="mb-2 flex items-center justify-between">
          <Text as="h3" variant="studio/body/medium/sm">
            Recent favorites
          </Text>
          <Link href={getHomepageUrl('/profile/favorites')}>
            <Button variant="tertiary">View All</Button>
          </Link>
        </div>
        <GalleryList
          asyncState={favoritesRecords}
          variant="grid"
          maxRecords={4}
          onCardRender={(job: Job) => (
            <p>
              <Text
                variant="web/body/md"
                className="opacity-50 dark:opacity-70"
              >
                <DaysAgo date={job.createdAt} />
              </Text>
            </p>
          )}
        />
      </div>
      <div className="mt-8">
        <div className="mb-2 flex items-center justify-between">
          <Text as="h3" variant="studio/body/medium/sm">
            Recent downloads
          </Text>
          <Link href={getHomepageUrl('/profile/download-history')}>
            <Button variant="tertiary">View All</Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:px-0">
          {downloadHistory.data && downloadHistory.data.items.length > 0 ? (
            <>
              {downloadHistory.data.items.map((item) => (
                <ProjectCard
                  key={item.projectID}
                  title={item.projectName}
                  type={
                    item.projectType === ProjectType.MOTION ? 'animate' : 'pose'
                  }
                  href={`${getStudioUrl()}${
                    item.projectType === ProjectType.MOTION
                      ? `/motion/${item.projectID}`
                      : `/pose/${item.projectID}`
                  }
                  `}
                  preview={item.mp4Path}
                  lastUpdatedAt={item.dateRequested}
                />
              ))}
            </>
          ) : (
            <Text
              as="div"
              variant="studio/body/sm"
              className="text-secondary border-primary col-span-full rounded-lg border p-4 text-center"
            >
              No downloads found
            </Text>
          )}
        </div>

        {/* <GalleryList
          asyncState={{
            status: downloadHistory.status === 'pending' ? 'pending' : 'ready',
            data: {
              items: downloadHistory.data?.items.map(convertToJobLike) as Job[],
            },
          }}
          variant="grid"
          maxRecords={4}
          onCardRender={(job: Job) => (
            <p>
              <Text
                variant="web/body/md"
                className="opacity-50 dark:opacity-70"
              >
                {job.sessionName}
              </Text>
            </p>
          )}
        /> */}
      </div>
    </div>
  );
}
